import type {
  DashboardFilters,
  DeveloperMetrics,
  LeadershipMetrics,
  ScrumMasterMetrics,
} from '@delivery-metrics/shared';
import axios from 'axios';

import { getAuthUrl, getDevToolsConfig } from '../config/env';
import { apiClient } from '../lib/api-client';

import { MockDataService } from './mock-data';

/**
 * Dashboard API Service
 * Provides methods for fetching dashboard data from the backend
 */
export class DashboardApiService {
  /**
   * Get Leadership Dashboard metrics
   */
  static async getLeadershipMetrics(
    filters: DashboardFilters
  ): Promise<LeadershipMetrics> {
    // Use mock data if enabled
    if (getDevToolsConfig().mockData) {
      // Simulate minimal network delay
      await new Promise(resolve => setTimeout(resolve, 50));
      return MockDataService.getLeadershipMetrics(filters);
    }

    const fallback = MockDataService.getLeadershipMetrics(filters);

    const params: Record<string, string | string[]> = {};
    if (filters?.timeRange?.start) {
      const d = new Date(filters.timeRange.start);
      params.startDate = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;
    }
    if (filters?.timeRange?.end) {
      const d = new Date(filters.timeRange.end);
      params.endDate = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}T23:59:59Z`;
    }
    if (filters?.programs?.length) {
      params.programs = filters.programs;
    }
    if (filters?.units?.length) {
      params.units = filters.units;
    }

    // Use Python API leadership endpoint and map shape to shared LeadershipMetrics.
    let response;
    try {
      response = await apiClient.get<any>('/api/metrics/leadership', {
        params,
      });
    } catch (error: any) {
      if (error?.response?.status === 404) {
        console.warn(
          'Leadership metrics endpoint not found at /api/metrics/leadership. Falling back to mock leadership metrics.'
        );
        return fallback;
      }
      throw error;
    }

    if (!response.success || !response.data) {
      throw new Error(response.error || 'Failed to fetch leadership metrics');
    }

    const payload = response.data;
    const innovation = payload?.innovationEffectiveness || {};
    const kpis = payload?.kpis || {};

    const featuresDeliveredValue = Number(kpis?.featuresDelivered || 0);
    const avgInvestment = kpis?.averageFeatureTimeInvestment || {};
    const timeEfficiency = kpis?.timeEfficiency || {};
    const velocityTrend = innovation?.deliveryVelocityIncrease?.trendData || [];

    return {
      ...fallback,
      kpis: {
        ...fallback.kpis,
        averageFeatureTimeInvestment: {
          ...fallback.kpis.averageFeatureTimeInvestment,
          hours: Number(avgInvestment?.hours || 0),
          weeks: Number(avgInvestment?.weeks || 0),
          months: Number(avgInvestment?.months || 0),
        },
        featuresDelivered: {
          ...fallback.kpis.featuresDelivered,
          value: featuresDeliveredValue,
        },
        timeEfficiency: {
          ...fallback.kpis.timeEfficiency,
          value: Number(timeEfficiency?.efficiency || 0),
          deliveredValue: Number(timeEfficiency?.deliveredValue || 0),
          effortInvestment: Number(timeEfficiency?.effortInvestment || 0),
          valueMethod:
            timeEfficiency?.measurementMethod?.type === 'FeatureCount'
              ? 'FeatureCount'
              : timeEfficiency?.measurementMethod?.type === 'UserImpactRating'
                ? 'UserImpactRating'
                : timeEfficiency?.measurementMethod?.type === 'Custom'
                  ? 'Custom'
                  : 'BusinessValueScore',
        },
      },
      featureDeliveryTrends: Array.isArray(velocityTrend)
        ? velocityTrend.map((point: any) => ({
            timestamp: new Date(point.timestamp),
            value: Number(point.value || 0),
          }))
        : fallback.featureDeliveryTrends,
    };
  }

  /**
   * Get Scrum Master Dashboard metrics
   */
  static async getScrumMasterMetrics(
    filters: DashboardFilters
  ): Promise<ScrumMasterMetrics> {
    // Python API doesn't have scrum master endpoint yet, use mock data
    await new Promise(resolve => setTimeout(resolve, 50));
    return MockDataService.getScrumMasterMetrics(filters);
  }

  /**
   * Get Developer Dashboard metrics
   */
  static async getDeveloperMetrics(
    filters: DashboardFilters
  ): Promise<DeveloperMetrics> {
    // Python API doesn't have developer endpoint yet, use mock data
    await new Promise(resolve => setTimeout(resolve, 50));
    return MockDataService.getDeveloperMetrics(filters);
  }
}

/**
 * Teams and Programs API Service
 * Provides methods for fetching organizational data
 */
export class OrganizationApiService {
  /**
   * Get available teams
   */
  static async getTeams(): Promise<string[]> {
    try {
      const response = await apiClient.get<string[]>('/api/organization/teams');
      if (response.success && response.data) {
        return response.data;
      }
    } catch (error) {
      console.warn('Falling back to mock teams', error);
    }

    await new Promise(resolve => setTimeout(resolve, 20));
    return MockDataService.getTeams();
  }

  /**
   * Get available programs
   */
  static async getPrograms(): Promise<string[]> {
    try {
      const response = await apiClient.get<string[]>(
        '/api/organization/programs'
      );
      if (response.success && response.data) {
        return response.data;
      }
    } catch (error) {
      console.warn('Falling back to mock programs', error);
    }

    await new Promise(resolve => setTimeout(resolve, 20));
    return MockDataService.getPrograms();
  }

  /**
   * Get available business units
   */
  static async getUnits(): Promise<string[]> {
    try {
      const response = await apiClient.get<string[]>('/api/organization/units');
      if (response.success && response.data && response.data.length > 0) {
        return response.data;
      }
    } catch (error) {
      console.warn('Falling back to mock units', error);
    }

    await new Promise(resolve => setTimeout(resolve, 20));
    return MockDataService.getUnits();
  }

  /**
   * Get mapping of unit to programs
   */
  static async getUnitProgramMap(): Promise<Record<string, string[]>> {
    try {
      const response = await apiClient.get<Record<string, string[]>>(
        '/api/organization/unit-program-map'
      );
      if (
        response.success &&
        response.data &&
        Object.keys(response.data).length > 0
      ) {
        return response.data;
      }
    } catch (error) {
      console.warn('Falling back to mock unit-program map', error);
    }

    await new Promise(resolve => setTimeout(resolve, 20));
    return MockDataService.getUnitProgramMap();
  }

  /**
   * Get available repositories
   */
  static async getRepositories(): Promise<string[]> {
    try {
      const response = await apiClient.get<string[]>(
        '/api/organization/repositories'
      );
      if (response.success && response.data) {
        return response.data;
      }
    } catch (error) {
      console.warn('Falling back to mock repositories', error);
    }

    await new Promise(resolve => setTimeout(resolve, 20));
    return MockDataService.getRepositories();
  }
}

/**
 * Authentication API Service
 */
export class AuthApiService {
  /**
   * Logout user
   */
  static async logout(): Promise<void> {
    await axios.get(getAuthUrl('/auth/logout'), { withCredentials: true });
    apiClient.clearAuth();
  }

  /**
   * Get current user profile
   */
  static async getCurrentUser(): Promise<any> {
    const response = (await axios.get(getAuthUrl('/auth/me'), {
      withCredentials: true,
    })) as any;

    const payload = response.data;

    if (payload && typeof payload === 'object') {
      if ('success' in payload) {
        if (!payload.success || !payload.data) {
          throw new Error(payload.error || 'Failed to fetch user profile');
        }

        return payload.data;
      }

      return payload;
    }

    throw new Error('Failed to fetch user profile');
  }
}
